import React, { useEffect, useMemo, useRef, useState } from "react";
import TimedOrderCard, { OrderCountdown } from "./TimedOrderCard";
import KdsTableReference from "./KdsTableReference";
import { formatReceivedTime } from "../orderTiming";

const SUB_ORDER_TYPE_MAP = {
  DINE_IN: "Dine in",
  TAKE_AWAY: "Take away",
  TAKEAWAY: "Take away",
  TAKE_OUT: "Take away",
  DELIVERY: "Delivery",
  WHATSAPP_DINE_IN: "Dine in",
};
const platformName = {
  ZOMATO_ORDER: "Zomato",
  SWIGGY_ORDER: "Swiggy",
  WHATSAPP_ORDER: "WhatsApp",
  UBER_EATS_ORDER: "Uber Eats",
  B2C_ORDER: "Website",
};

const normalizedOrderType = (value) => String(value ?? "")
  .trim()
  .replace(/[\s-]+/g, "_")
  .toUpperCase();

function getRushOrderLabel(order) {
  const platform = platformName[normalizedOrderType(order?.platform)] ||
    platformName[normalizedOrderType(order?.orderType)];
  if (platform) return { label: platform, isDineIn: false };

  const subType = SUB_ORDER_TYPE_MAP[normalizedOrderType(order?.metadata?.subOrderType)];
  const orderType = SUB_ORDER_TYPE_MAP[normalizedOrderType(order?.orderType)];
  const explicitType = subType || orderType;
  if (explicitType) return { label: explicitType, isDineIn: explicitType === "Dine in" };

  const tableNumber = order?.metadata?.tableNumber;
  const hasTable = tableNumber != null && String(tableNumber).trim() !== "";
  return hasTable ? { label: "Dine in", isDineIn: true } : { label: "Order", isDineIn: false };
}

function RushDish({ order, item, confirmed, pending, saving, checked, onToggleItem }) {
  const terminal = item?.taskStatus === "COMPLETED" || item?.taskStatus === "READY" || item?.taskStatus === "REMOVED" ||
    ["COMPLETED", "READY", "REMOVED", "CANCELLED", "CANCELED", "REJECTED"].includes(item?.status);
  const disabled = pending || confirmed || terminal || item?.taskId == null || item?.status !== "IN_PROGRESS" ||
    typeof onToggleItem !== "function";
  const title = item?.name || "Unnamed item";
  const isChecked = !!checked || !!confirmed || item?.taskStatus === "COMPLETED" ||
    ["COMPLETED", "READY"].includes(item?.status) || item?.taskStatus === "READY";
  const orderRef = order?.token || order?.id;
  return (
    <div className={`rush-dish${isChecked ? " is-ready" : ""}${terminal ? " is-terminal" : ""}`}>
      <button
        type="button"
        className="rush-dish-action"
        aria-label={`${checked ? "Uncheck" : "Check"} ${title} in order ${orderRef}`}
        aria-pressed={isChecked}
        aria-busy={saving}
        disabled={disabled}
        onClick={() => onToggleItem(order.id, item.taskId)}
      >
        <span className="rush-dish-name">{title}</span>
        <span className="rush-dish-qty">×{item?.qty ?? 0}</span>
        {isChecked
          ? <span className="rush-dish-state" aria-hidden="true">✓</span>
          : saving ? (
            <span className="rush-dish-state">
              <span className="loader kds-btn-loader" aria-hidden="true" />Updating
            </span>
          ) : null}
      </button>
      {(item?.metadata?.isBundle || item?.metadata?.isComplimentary || item?.note || item?.addons?.length > 0) && (
        <div className="rush-dish-details">
          {item?.metadata?.isBundle && <span className="rush-detail-pill">Combo</span>}
          {item?.metadata?.isComplimentary && <span className="rush-detail-pill">Complimentary</span>}
          {item?.note && <span className="rush-detail-pill rush-note">{item.note}</span>}
          {(item?.addons || []).map((addon, index) => (
            <span className="rush-detail-pill" key={`${item.taskId}-addon-${index}`}>
              + {typeof addon === "string" ? addon : addon?.itemName || addon?.name || addon?.title || "Add-on"}
              {Number(addon?.quantity) > 1 ? ` ×${addon.quantity}` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RushTicket({ order, ordinal, confirmedReadyItems, checkedItems, pending, savingTaskIds, onToggleItem, onBumpOrder, onEdit, highlighted }) {
  const items = Array.isArray(order?.items) ? order.items : [];
  const orderLabel = getRushOrderLabel(order);
  const tableNumber = order?.metadata?.tableNumber;
  const hasTable = tableNumber != null && String(tableNumber).trim() !== "";
  const orderRef = order?.token || order?.id;
  const hasLiveTask = items.some((item) => item?.taskId != null && item?.status === "IN_PROGRESS" &&
    item?.taskStatus !== "COMPLETED" && item?.taskStatus !== "REMOVED" &&
    !["COMPLETED", "REMOVED", "CANCELLED", "CANCELED", "REJECTED"].includes(item?.status));
  const canComplete = hasLiveTask && !pending && typeof onBumpOrder === "function";
  return (
    <TimedOrderCard order={order} className={`kds-order-card rush-ticket${highlighted ? " is-highlighted" : ""}`}>
      <header className="rush-ticket-header">
        <button type="button" className="rush-ticket-complete" disabled={!canComplete}
          aria-label={`Complete order ${orderRef}`} aria-busy={pending ? "true" : "false"}
          onClick={async () => {
            if (!canComplete) return;
            try { await onBumpOrder(order); } catch (_) { /* Parent owns error feedback; never claim completion locally. */ }
          }}>
          <div className="rush-ticket-header-content">
            <span className="rush-ticket-title">
              <span className="rush-ticket-number" aria-label={`Ticket ${ordinal}`}>{ordinal}</span>
              <span className="rush-order-type">{orderLabel.label}</span>
              {orderLabel.isDineIn && hasTable && (
                <span className="rush-table-reference">
                  <KdsTableReference tableNumber={tableNumber} showPrefix />
                </span>
              )}
            </span>
            <div className="rush-ticket-meta">
              <span>{formatReceivedTime(order?.created)}</span>
              <span className="rush-job-id">Order {order?.id}</span>
              <span>{order?.metadata?.userName || "Guest"}</span>
            </div>
            <div className="rush-ticket-countdown"><OrderCountdown order={order} showDescription={false} /></div>
            {pending && (
              <span className="rush-ticket-busy" role="status">
                <span className="loader kds-btn-loader" aria-hidden="true" />
                {savingTaskIds?.length && savingTaskIds.length < items.length ? "Saving…" : "Completing…"}
              </span>
            )}
            {highlighted && <span className="rush-highlight-badge">Dish match</span>}
          </div>
        </button>
        {typeof onEdit === "function" && <button type="button" className="rush-edit-button"
          onClick={(event) => { event.stopPropagation(); onEdit(order); }}
          aria-label={`Edit order ${orderRef}`} title="Edit order">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5 4 4M4 20l4.2-.9L19 8.3a2.1 2.1 0 0 0-3-3L5.2 16.1 4 20Z" /></svg>
        </button>}
      </header>
      <div className="rush-ticket-dishes">
        {items.map((item, index) => {
          const confirmed = (confirmedReadyItems?.[order.id] || []).includes(item?.taskId);
          const jobChecked = checkedItems?.[order.id];
          const checked = Array.isArray(jobChecked) ? jobChecked.includes(item?.taskId)
            : !!(jobChecked && (jobChecked[item?.taskId] ?? jobChecked[String(item?.taskId)]));
          return <RushDish key={item?.taskId || item?.id || index} order={order} item={item}
            confirmed={confirmed} pending={pending} checked={checked} onToggleItem={onToggleItem}
            saving={pending && (!savingTaskIds || savingTaskIds.includes(item?.taskId))} />;
        })}
      </div>
    </TimedOrderCard>
  );
}

export default function RushOrdersBoard({
  orders = [], ordinalById = {}, confirmedReadyItems = {}, buttonLoading = {}, savingTaskIds = {}, checkedItems = {},
  onToggleItem, onBumpOrder, highlightedOrderIds = [], highlightKey = null, onEdit,
  loading = false, error = "", onRetry,
}) {
  const [page, setPage] = useState(0);
  const lastJumpedHighlight = useRef(null);
  const pageCount = Math.max(1, Math.ceil(orders.length / 15));
  const safePage = Math.min(page, pageCount - 1);
  const pageOrders = useMemo(() => orders.slice(safePage * 15, safePage * 15 + 15), [orders, safePage]);
  const columns = useMemo(() => Array.from({ length: 5 }, (_, columnIndex) =>
    pageOrders.slice(columnIndex * 3, columnIndex * 3 + 3)), [pageOrders]);
  const highlightedIds = useMemo(() => new Set((highlightedOrderIds || []).map(String)), [highlightedOrderIds]);
  const hasHighlightMatch = orders.some((order) => highlightedIds.has(String(order?.id)));

  // Keep the current page stable as data refreshes; only clamp if the last page vanished.
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  useEffect(() => {
    if (highlightKey == null) {
      lastJumpedHighlight.current = null;
      return;
    }
    if (lastJumpedHighlight.current === highlightKey) return;
    const firstMatch = orders.findIndex((order) => highlightedIds.has(String(order?.id)));
    if (firstMatch >= 0) {
      setPage(Math.floor(firstMatch / 15));
      lastJumpedHighlight.current = highlightKey;
    }
  }, [highlightKey, highlightedIds, orders]);

  if (error && !orders.length) {
    return (
      <section className="rush-board-state" role="alert">
        <strong>Rush tickets unavailable</strong>
        <span>Orders could not be loaded. No empty-board status is shown until data is available.</span>
        {onRetry && (
          <button type="button" onClick={onRetry} disabled={loading} aria-busy={loading}>
            {loading && <span className="loader kds-btn-loader" aria-hidden="true" />}
            {loading ? "Retrying…" : "Retry loading tickets"}
          </button>
        )}
      </section>
    );
  }
  if (loading && !orders.length) {
    return <section className="rush-board-loading" aria-label="Loading rush tickets" aria-busy="true">
      {Array.from({ length: 5 }, (_, index) => <div className="rush-skeleton-column" key={index}>
        {Array.from({ length: 2 }, (_, card) => <div className="rush-skeleton-ticket" key={card} />)}
      </div>)}
    </section>;
  }
  if (!orders.length) {
    return <section className="rush-board-state" role="status">
      <strong>All clear</strong>
      <span>No active dishes are waiting.</span>
      {highlightKey != null && <span className="rush-highlight-empty">No matching processing tickets yet.</span>}
    </section>;
  }

  return (
    <section className="rush-board-wrap" aria-label="Rush order tickets">
      {error && <div className="rush-inline-error" role="alert">
        <span>Ticket data may be out of date.</span>{onRetry && (
          <button type="button" onClick={onRetry} disabled={loading} aria-busy={loading}>
            {loading && <span className="loader kds-btn-loader" aria-hidden="true" />}
            {loading ? "Retrying…" : "Retry"}
          </button>
        )}
      </div>}
      {highlightKey != null && !hasHighlightMatch && (
        <div className="rush-highlight-empty" role="status">
          No matching processing tickets yet.
        </div>
      )}
      <div className="rush-board-scroll" aria-label="Rush ticket columns">
        <div className="rush-board">
          {columns.map((column, columnIndex) => (
            <div className="rush-column" key={columnIndex} aria-label={`Rush column ${columnIndex + 1}`}>
              {column.map((order, rowIndex) => {
                const ordinal = ordinalById[order.id] ?? safePage * 15 + columnIndex * 3 + rowIndex + 1;
                return <RushTicket key={order.id} order={order} ordinal={ordinal}
                  confirmedReadyItems={confirmedReadyItems}
                  checkedItems={checkedItems}
                  pending={buttonLoading[order.id] === "ready" || !!buttonLoading[order.id]}
                  savingTaskIds={savingTaskIds[order.id]}
                  onToggleItem={onToggleItem} onBumpOrder={onBumpOrder} onEdit={onEdit}
                  highlighted={highlightedIds.has(String(order.id))} />;
              })}
            </div>
          ))}
        </div>
      </div>
      <nav className="rush-pagination" aria-label="Rush ticket pages">
        <span aria-live="polite">Tickets {safePage * 15 + 1}–{Math.min((safePage + 1) * 15, orders.length)} of {orders.length}</span>
        <div>
          <button type="button" onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={safePage === 0} aria-label="Previous rush tickets">Previous</button>
          <span className="rush-page-indicator" aria-current="page">Page {safePage + 1} of {pageCount}</span>
          <button type="button" onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
            disabled={safePage >= pageCount - 1} aria-label="Next rush tickets">Next</button>
        </div>
      </nav>
    </section>
  );
}
